import { useCallback, useEffect, useState } from 'react';
import { safeGetItem, safeSetItem, safeRemoveItem } from '@/lib/safe-storage';

/**
 * A hunt participant session persisted in localStorage so progress
 * survives page reloads on the same device. No account required.
 *
 * Storage access is wrapped via `safe-storage` so Safari private mode
 * or blocked storage never crashes the page — the session simply won't
 * persist across reloads in that case.
 */
export interface HuntSession {
  huntSlug: string;
  participantId: string;
  token: string;
  nickname: string;
  team: string | null;
}

const STORAGE_PREFIX = 'hunt-session:';

function storageKey(slug: string): string {
  return `${STORAGE_PREFIX}${slug}`;
}

function readSession(slug: string): HuntSession | null {
  const raw = safeGetItem(storageKey(slug));
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as HuntSession;
    if (
      typeof parsed?.huntSlug === 'string' &&
      typeof parsed?.participantId === 'string' &&
      typeof parsed?.token === 'string' &&
      parsed.huntSlug === slug
    ) {
      return parsed;
    }
  } catch {
    // corrupt or missing — treat as no session
  }
  return null;
}

function writeSession(session: HuntSession): void {
  safeSetItem(storageKey(session.huntSlug), JSON.stringify(session));
}

function removeSession(slug: string): void {
  safeRemoveItem(storageKey(slug));
}

/**
 * Manage a hunt participant session for the given slug.
 * The session is stored in localStorage and restored on revisit.
 */
export function useHuntSession(slug: string): {
  session: HuntSession | null;
  saveSession: (session: HuntSession) => void;
  clearSession: () => void;
} {
  const [session, setSession] = useState<HuntSession | null>(() => readSession(slug));

  // Re-read when slug changes (navigating between hunts)
  useEffect(() => {
    setSession(readSession(slug));
  }, [slug]);

  const saveSession = useCallback((next: HuntSession) => {
    writeSession(next);
    setSession(next);
  }, []);

  const clearSession = useCallback(() => {
    removeSession(slug);
    setSession(null);
  }, [slug]);

  return { session, saveSession, clearSession };
}

/**
 * Build RequestInit headers carrying the participant bearer token.
 * Passed to generated hooks via the `request` option.
 */
export function huntAuthHeaders(token: string): { headers: Record<string, string> } {
  return { headers: { Authorization: `Bearer ${token}` } };
}

// ── Pending check-in (cold-scan redirect) ────────────────────────────────

const PENDING_CHECKIN_KEY = 'hunt-pending-checkin';

export interface PendingCheckin {
  slug: string;
  stationCode: string;
}

/**
 * Stash a station code so the hunt page can auto-navigate to the check-in
 * route after the visitor completes the quick-join flow.
 */
export function stashPendingCheckin(slug: string, stationCode: string): void {
  safeSetItem(PENDING_CHECKIN_KEY, JSON.stringify({ slug, stationCode }));
}

/**
 * Read and clear a stashed pending check-in. Returns null if none.
 */
export function consumePendingCheckin(): PendingCheckin | null {
  const raw = safeGetItem(PENDING_CHECKIN_KEY);
  if (!raw) return null;
  safeRemoveItem(PENDING_CHECKIN_KEY);
  try {
    const parsed = JSON.parse(raw) as PendingCheckin;
    if (typeof parsed?.slug === 'string' && typeof parsed?.stationCode === 'string') {
      return parsed;
    }
  } catch {
    // corrupt — ignore
  }
  return null;
}
